import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'lucide-react';
import { GuestBottomNav } from '../GuestBottomNav';
import NotificationBellButton from '../NotificationBellButton';
import { RouteTransitionLogo } from '../RouteTransitionLogo';
import PageInfoButton from '../PageInfoButton';
import WelcomeTourSheet from '../WelcomeTourSheet';
import { guestQrTourSlides } from '../welcomeTourSlides';

const FONT = "'Inria Serif', serif";

// Extract an invite ID from any allurexp.com or preview URL pasted by the user.
// Invite codes come in TWO formats:
//   • Manual invites (from messaging_helpers.py) → `XXXX-XXXX` (e.g. `A1B2-C3D4`).
//   • Automatic iCal invites (from ical_feed_sync.py) → `ALL-XXXXXXXX` (e.g.
//     `ALL-A1B2C3D4`). Both must be accepted — the auto-generated iCal invites
//     were silently rejected before this fix because the regex only matched
//     the 4-4 pattern.
// The bounded quantifiers ({8} and {4}-{4}) also guard against Android WebView
// occasionally double-firing paste events (onPaste + onChange), which produced
// concatenated URLs like `.../invite/BF43-8C25https://.../invite/BF43-8C25`
// and yielded `BF43-8C25HTTPS` under a looser regex. Normalize to uppercase
// for the backend lookup.
function extractInviteIdFromUrl(raw) {
  if (!raw) return null;
  const m = raw.match(/\/invite\/(ALL-[A-Z0-9]{8}|[A-Z0-9]{4}-[A-Z0-9]{4})/i);
  return m ? m[1].trim().toUpperCase() : null;
}

export default function GuestQREmptyState({ navigate, tourOpen, setTourOpen, handleCloseTour }) {
  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState('');

  const handlePaste = (val) => {
    setLinkInput(val);
    setLinkError('');
    const inviteId = extractInviteIdFromUrl(val);
    if (inviteId) {
      navigate(`/invite/${inviteId}`);
    }
  };

  const handleManualGo = () => {
    const inviteId = extractInviteIdFromUrl(linkInput);
    if (inviteId) {
      navigate(`/invite/${inviteId}`);
    } else {
      setLinkError("That doesn't look like a valid Allure invite link. Make sure you paste the full link.");
    }
  };

  const cardBase = {
    width: '100%',
    maxWidth: 320,
    borderRadius: 20,
    background: 'var(--surface)',
    border: '1px solid var(--card-border)',
    boxShadow: 'var(--premium-shadow-main)',
    padding: '18px 20px',
  };

  return (
    <div className="app-shell" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageInfoButton onClick={() => setTourOpen(true)} testId="guest-qr-page-info-button" />
        <NotificationBellButton buttonTestId="guest-qr-empty-bell-button" popupTestId="guest-qr-empty-notification-popup" />
      </div>
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0, gap: 12 }}>
        <RouteTransitionLogo width={140} height={125} dataTestId="guest-qr-empty-shared-logo" />

        {/* Card 1: heading */}
        <motion.div
          data-testid="guest-qr-empty-heading-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ ...cardBase, textAlign: 'center' }}
        >
          <p style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35, margin: 0 }}>
            Your access is provided by your host
          </p>
        </motion.div>

        {/* Card 2: instructions */}
        <motion.div
          data-testid="guest-qr-empty-instructions-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.18 }}
          style={{ ...cardBase, textAlign: 'center' }}
        >
          <p style={{ fontFamily: FONT, fontSize: 16, fontWeight: 400, color: 'var(--text-2)', lineHeight: 1.5, margin: 0 }}>
            Ask your Airbnb host for your Allure access link to unlock your exclusive discounts during your stay.
          </p>
        </motion.div>

        {/* Card 3: paste link manually */}
        <motion.div
          data-testid="guest-qr-empty-paste-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.26 }}
          style={{ ...cardBase }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Link size={16} color="var(--accent-purple)" strokeWidth={2} />
            <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Already have the link?
            </p>
          </div>
          <p style={{ fontFamily: FONT, fontSize: 13, color: 'var(--text-3)', margin: '0 0 10px', lineHeight: 1.4 }}>
            Paste the invite link your host sent you and we'll take you straight there.
          </p>
          <input
            data-testid="guest-qr-paste-link-input"
            type="url"
            value={linkInput}
            onChange={(e) => handlePaste(e.target.value)}
            onPaste={(e) => {
              // Prevent the browser default paste so it doesn't also insert the
              // text on top of what onChange will receive. On Android WebView
              // both onPaste + native input event were firing, producing a
              // doubled value like `https://.../invite/BF43-8C25https://.../invite/BF43-8C25`.
              e.preventDefault();
              const pasted = (e.clipboardData || window.clipboardData).getData('text');
              handlePaste(pasted);
            }}
            placeholder="Paste invite link here…"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '11px 14px',
              borderRadius: 12,
              border: `1px solid ${linkError ? 'var(--status-danger-text)' : 'var(--card-border)'}`,
              background: 'var(--surface-2)',
              color: 'var(--text)',
              fontFamily: FONT,
              fontSize: 15,
              outline: 'none',
            }}
          />
          {linkError ? (
            <p data-testid="guest-qr-paste-link-error" style={{ fontFamily: FONT, fontSize: 13, color: 'var(--status-danger-text)', margin: '6px 0 0', lineHeight: 1.3 }}>
              {linkError}
            </p>
          ) : null}
          {linkInput && !extractInviteIdFromUrl(linkInput) ? (
            <button
              data-testid="guest-qr-paste-link-go"
              onClick={handleManualGo}
              style={{
                marginTop: 10,
                width: '100%',
                padding: '11px 0',
                borderRadius: 12,
                border: 'none',
                background: 'var(--accent-purple)',
                color: '#FFF',
                fontFamily: FONT,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Open invite
            </button>
          ) : null}
        </motion.div>
      </div>
      <GuestBottomNav />

      <WelcomeTourSheet
        open={tourOpen}
        onClose={handleCloseTour}
        slides={guestQrTourSlides}
        testIdPrefix="guest-qr-page-tour"
      />
    </div>
  );
}
